import { translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import {
    StyledButtonsWrapper,
    StyledFiltersSection,
    StyledFiltersWrapper,
    StyledSideFiltersWrapper,
} from "./styled-components";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { FilterBarButtonWithModal, SearchBar } from "@kaltura/mediaspace-shared-search";
import { FilterProps, FilterValues, SupportedKmsTypeFilter } from "@kaltura/mediaspace-shared-types";
import { useEffect } from "react";
import Filters from "./filters/Filters";
import { buildQuerystring } from "kaltura-typescript-client/adapters/utils";
import CreateChannelButton from "./CreateChannelButton";
import { useChannelsContext } from "../ChannelsContext";

export interface FiltersSectionProps {
    /**
     * filters
     */
    filters: FilterProps<SupportedKmsTypeFilter>[];

    /**
     * filters values
     */
    filterValues: FilterValues;

    /**
     * keyword submit handler
     */
    onKeywordSubmit: (value: string) => void;

    /**
     * filter change handler
     */
    onFilterChange: (values: FilterValues) => void;

    /**
     * should reflect selected filters in query string
     * default: true
     * if false, the query string will not be updated when filters change
     * this is useful when the component is used in a context where the query string is not relevant (e.g. gallery channels tab)
     */
    reflectSelectedFiltersInQueryString?: boolean;
}

const FiltersSection = (props: FiltersSectionProps) => {
    const {
        filters,
        filterValues,
        onKeywordSubmit,
        onFilterChange,
        reflectSelectedFiltersInQueryString = true,
    } = props;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

    const { allowCreateChannel } = useChannelsContext();

    // update history on filter change
    useEffect(() => {
        if (!reflectSelectedFiltersInQueryString) {
            return;
        }
        // update query string
        const queryString = buildQuerystring(filterValues);
        window.history.pushState(null, "", `?${queryString}`);
    }, [filterValues, reflectSelectedFiltersInQueryString]);

    return (
        <StyledFiltersSection columnView={isXsScreen} className={"kms-ds-channels-filters-container"}>
            <SearchBar
                keywordSearchProps={{
                    onSubmit: onKeywordSubmit,
                    value: filterValues["keyword"] as string,
                    placeholder: translate("Search for channels"),
                    ariaLabel: translate("Search for channels"),
                }}
                fullWidth={isXsScreen}
            />

            {!isSmallScreen && (
                <StyledFiltersWrapper>
                    <StyledSideFiltersWrapper>
                        <Filters filters={filters} values={filterValues} onChange={onFilterChange} />
                        {allowCreateChannel && <CreateChannelButton />}
                    </StyledSideFiltersWrapper>
                </StyledFiltersWrapper>
            )}

            {isSmallScreen && (
                <StyledButtonsWrapper>
                    <FilterBarButtonWithModal
                        filters={filters}
                        values={filterValues}
                        onChange={onFilterChange}
                        noClearAll={true}
                    />
                    {allowCreateChannel && <CreateChannelButton />}
                </StyledButtonsWrapper>
            )}
        </StyledFiltersSection>
    );
};

export default FiltersSection;
